import { ICareMap, IHouseholdMember, INewCareMap, IPatient } from '../caremap/ICareMap';
import { NameHelper } from '../contact-methods/NameHelper';
import { IAvatarIdentity } from './IAvatarIdentity';
import { ILightUser } from '../user/UserState';

type IPicturedUser = Partial<Pick<ILightUser, 'pictureUrl' | 'avatar' | 'color' | 'links'>>;

/** Everything a care recipient's avatar needs, so no renderer can take the identity and miss the rest. */
export interface ICareRecipientAvatar {
    identity: IAvatarIdentity;
    name: string;
}

interface IForCareRecipient {
    (careMap: ICareMap | INewCareMap): ICareRecipientAvatar;
    // with no care map yet, an avatar that has not loaded
    (careMap: ICareMap | INewCareMap | undefined): ICareRecipientAvatar | { identity: undefined; name: string; };
}

export class AvatarIdentityFactory {
    static inject = () => [NameHelper];
    constructor(private names: NameHelper) {}

    // A care recipient or care map member as the api sent them, link included. A photo still
    // previewing from photoDataURI is not drawn.
    forMember = (member: IPatient | IHouseholdMember): IAvatarIdentity => ({
        photo: member.photo,
        avatar: member.avatar,
        color: member.color,
        links: member.links
    });

    // An app user as the api sent them: the account itself, a note or comment's author, a
    // collaborator. The picture link is the audited route to their picture; with none offered (a
    // picture we do not host, or an api older than the route) the picture is read as stored.
    forUser = (user: IPicturedUser): IAvatarIdentity => ({
        photo: user.pictureUrl,
        avatar: user.avatar,
        color: user.color,
        links: user.links?.picture ? { photo: user.links.picture } : undefined,
        photoHasNoAuditedRoute: user.links?.picture ? undefined : true
    });

    // A person with access to a care map: drawn as the care map member linked to them where that
    // member has a photo, otherwise as the app user the api projected, keeping the member's chosen
    // illustration or colour over the user's.
    forCollaborator = (params: { member?: IPatient | IHouseholdMember; user?: IPicturedUser }): IAvatarIdentity => {
        const { member, user } = params;
        if (member?.photo) {
            return this.forMember(member);
        }
        const identity = this.forUser(user || {});
        return { ...identity, avatar: member?.avatar || identity.avatar, color: member?.color || identity.color };
    };

    // A care map's care recipient, with the name staff see as initials.
    forCareRecipient: IForCareRecipient = ((): IForCareRecipient => {
        const forLoadedCareMap = (careMap: ICareMap | INewCareMap): ICareRecipientAvatar => ({
            identity: this.forMember(careMap.patient),
            name: this.names.getName({ firstName: careMap.patient.firstName, lastName: careMap.patient.lastName })
        });

        function forCareRecipient(careMap: ICareMap | INewCareMap): ICareRecipientAvatar;
        function forCareRecipient(careMap: ICareMap | INewCareMap | undefined): ICareRecipientAvatar | { identity: undefined; name: string; };
        function forCareRecipient(careMap: ICareMap | INewCareMap | undefined): ICareRecipientAvatar | { identity: undefined; name: string; } {
            return careMap ? forLoadedCareMap(careMap) : { identity: undefined, name: '' };
        }
        return forCareRecipient;
    })();
}
